import {Component, Emitter} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {NotifyPropertyChanged} from '../utils/NotifyPropertyChanged.js';
import {Puzzle} from './puzzle.js';
import {TransactionalNames} from '../enums/TransactionalNames.js';
import {Statistics} from './statistics.js';
import {formatNumber} from '../utils/Utils.js';
import {FlatWith2ColorsOpaqueTextured} from '../types/materials.js';
import {CoroutineManager} from '../classes/coroutines/CoroutineManager.js';
import {Coroutine} from '../classes/coroutines/Coroutine.js';
import {waitForSeconds} from '../classes/coroutines/YieldInstructions.js';

/**
 * Base class for UI buttons that interact with the game state.
 * Handles cooldowns, inventory tracking, and visual state updates.
 * Emits property changes to update associated UI elements.
 * This component manages the behavior of buttons that have an associated inventory item
 * and a cooldown period after use. It interacts with the `Puzzle` and `Statistics`
 * components to manage game state and player inventory.
 */
export class ButtonBehavior extends Component implements NotifyPropertyChanged {
    static TypeName = 'button-behavior';

    @property.int(40)
    turnCooldown = 40;

    @property.enum(Object.keys(TransactionalNames).filter((e) => isNaN(Number(e))))
    statInventory: TransactionalNames;

    @property.color(1.0, 1.0, 1.0, 1.0)
    myInventoryDisplayColor = [1.0, 1.0, 1.0, 1.0];

    @property.string('')
    myInventoryDisplayText = '';

    @property.float(0)
    sliderValue = 0;

    cooldownMaterial: FlatWith2ColorsOpaqueTextured;

    protected amountSpent: number = 1;
    protected currentInventoryCount: number;
    protected currentCooldown: number;
    protected firstUse: boolean;
    protected lastTurn: number = 0;
    protected isInactive: boolean = false;
    protected haveFunds: boolean = false;

    private _gameOver: boolean;
    private _timer: number;
    private _puzzle: Puzzle;
    protected _coroutineManager = new CoroutineManager();

    /**
     * Called once per component instance, after all components were created and before
     * the first frame. Use this function to setup the component.
     */
    init() {}

    /**
     * Called once per component instance, after all components were created and before
     * the first frame. Use this function to setup the component.
     */
    start() {
        this.firstUse = false;

        this.currentInventoryCount = Statistics.instance.getTransactional(
            this.statInventory
        );

        this.currentCooldown = this.setCooldown();

        this.myInventoryDisplayText = formatNumber(this.currentInventoryCount);
        this.myInventoryDisplayColor = [1, 1, 1, 1];

        this.lastTurn = 0;
        this._puzzle = Puzzle.instance;
        this._timer = this._puzzle.delayBeforeGameStart;

        // this.sliderValue = ButtonBehaviorPersistance.instance.getSliderValue(
        //     this.statInventory
        // );
        // this.isInactive = ButtonBehaviorPersistance.instance.getIsInactiveValue(
        //     this.statInventory
        // );
        this.clearCooldown();
        // Utils.SetActiveRecursively(mask, false);

        // buttonClickSFX  = GetComponents<PlaySoundOnClick>();
        // buttonScale = GetComponent<UIButtonScale>();

        // foreach (PlaySoundOnClick obj in buttonClickSFX)
        // {
        // 	obj.enabled = false;
        // }

        // buttonScale.enabled = false;

        this.notifyStatsChanged();
    }

    /**
     * Called when the component is activated. Registers callbacks for game events.
     */
    onActivate(): void {
        Puzzle.instance.registerNewGameCallback(this.newGame);
        //Puzzle.instance.registerTurnCompleteCallback(this.turnsCompleted);
        Puzzle.instance.registerGameOverCallback(this._gameOverHandler);

        Statistics.instance.registerChangedCallback(this._onStatsChanged);
    }

    /**
     * Called when the component is deactivated. Unregisters callbacks for game events.
     */
    onDeactivate(): void {
        Puzzle.instance.unregisterNewGameCallback(this.newGame);
        //  Puzzle.instance.unregisterTurnCompleteCallback(this.turnsCompleted);
        Puzzle.instance.unregisterGameOverCallback(this._gameOverHandler);
        Statistics.instance.unRegisterChangedCallback(this._onStatsChanged);
    }

    _time = 0;
    /**
     * Called every frame. Updates the coroutine manager.
     * @param dt The time difference in seconds since the last frame.
     */
    update(dt: number) {
        this._time += dt;
        this._coroutineManager.update(dt);
    }

    propertyChanged: Emitter<[string]> = new Emitter();
    /**
     * Notifies listeners that a property has changed.
     * @param propertyName The name of the property that changed.
     */
    notifyPropertyChanged(propertyName: string): void {
        this.propertyChanged.notify(propertyName);
    }

    /**
     * Attempts to spend a specified amount of an inventory item.
     * @param statInventory The type of inventory item to spend.
     * @param amount The amount to spend.
     * @returns True if the inventory was spent successfully, false otherwise.
     */
    spendInventory(statInventory: TransactionalNames, amount: number): boolean {
        let sufficientFunds = false;
        let currentAmount = Statistics.instance.getTransactional(statInventory);

        if (currentAmount >= amount) {
            currentAmount -= amount;

            sufficientFunds = true;

            Statistics.instance.addAnonymousPurchaseTransaction(statInventory, -amount);
        }

        return sufficientFunds;
    }

    /**
     * Updates the cooldown material's cutoff position based on the slider value.
     */
    updateMaterial() {
        this.cooldownMaterial?.setCutoffPosition([0, this.sliderValue]);
    }
    /**
     * Sets the initial cooldown duration for the button.
     * Can be overridden by subclasses for custom cooldown logic.
     * @virtual
     * @returns The cooldown duration in turns.
     */
    protected setCooldown(): number {
        return this.turnCooldown;
    }

    /**
     * Callback function executed when turns are completed in the puzzle.
     * Updates the button's cooldown status and visual state.
     * @virtual
     * @param puzzle The Puzzle instance.
     */
    turnsCompleted = (puzzle: Puzzle) => {
        this.lastTurn++;

        if (this.isInactive == true) {
            // can use again
            if (this.lastTurn >= this.currentCooldown) {
                if (this.haveFunds == true) {
                    // decrement to 0
                    let currentSlider = this.sliderValue;
                    this._coroutineManager.stopCoroutine(
                        this._adjustLevelSliderDynamicallyId
                    );
                    this._adjustLevelSliderDynamicallyId =
                        this._coroutineManager.addCoroutine(
                            new Coroutine(
                                this._adjustLevelSliderDynamically(0.0, currentSlider)
                            )
                        );
                }

                this.isInactive = false;

                if (this._gameOver == false) {
                    //gameObject.GetComponent<Collider>().enabled = true;
                } else {
                    //gameObject.GetComponent<Collider>().enabled = false;
                }

                this.currentInventoryCount = Statistics.instance.getTransactional(
                    this.statInventory
                );

                this.myInventoryDisplayColor = [1, 1, 1, 1];
                this.myInventoryDisplayText = formatNumber(this.currentInventoryCount);
                this.notifyPropertyChanged('everything');
            } else {
                if (this.haveFunds == true) {
                    let currentSlider = this.sliderValue;
                    let percent = 1.0 - this.lastTurn / this.currentCooldown;
                    this._coroutineManager.stopCoroutine(
                        this._adjustLevelSliderDynamicallyId
                    );
                    this._adjustLevelSliderDynamicallyId =
                        this._coroutineManager.addCoroutine(
                            new Coroutine(
                                this._adjustLevelSliderDynamically(percent, currentSlider)
                            )
                        );
                }
            }
        }
    };

    _adjustLevelSliderDynamicallyId = 0;

    /**
     * Coroutine to smoothly adjust the cooldown slider visually.
     * @param newSlider The target slider value (0 to 1).
     * @param oldSlider The current slider value.
     * @protected
     */
    protected *_adjustLevelSliderDynamically(newSlider: number, oldSlider: number) {
        if (newSlider != 0) {
            let transitionTime = 0.2;
            let increment = (oldSlider - newSlider) / transitionTime;
            for (let i = 0; i < oldSlider - newSlider; i += increment / 60.0) {
                this.sliderValue = oldSlider - i;
                this.updateMaterial();
                yield* waitForSeconds(0.005);
            }
            // ButtonBehaviorPersistance.instance.setSliderValue(
            //     this.statInventory,
            //     this.sliderValue
            // );
        } else {
            this.sliderValue = 0;
            this.notifyPropertyChanged('everything');
            this.updateMaterial();
        }

        // if you are inactive now, make sure you are really 0
        if (this.isInactive == false) {
            this.sliderValue = 0;
            this.notifyPropertyChanged('everything');
            this.updateMaterial();
        }
    }

    /**
     * Callback function executed when a new game starts.
     * Resets the button's state, cooldown, and inventory count.
     * @virtual
     * @param puzzle The Puzzle instance.
     */
    newGame = (puzzle: Puzzle) => {
        //TK: this event is cleared when a game ends. To make sure it is added we'll have to load
        //    it here.
        Puzzle.instance.registerTurnCompleteCallback(this.turnsCompleted);

        this.firstUse = false;
        this._gameOver = false;
        this.currentInventoryCount = Statistics.instance.getTransactional(
            this.statInventory
        );
        // this.turnCooldown = 0;
        this.currentCooldown = this.setCooldown();

        this.myInventoryDisplayText = formatNumber(this.currentInventoryCount);
        this.myInventoryDisplayColor = [1, 1, 1, 1];

        this.lastTurn = 0;
        this.clearCooldown();

        this.notifyStatsChanged();

        // first time playing with this button? then show tutorial
        if (this.shouldShowTutorial()) {
            this.showTutorial();
        }
    };

    /**
     * Determines if the tutorial for this button should be shown.
     * Can be overridden by subclasses.
     * @returns True if the tutorial should be shown, false otherwise.
     * @protected
     */
    protected shouldShowTutorial(): boolean {
        return false;
    }

    /**
     * Shows the tutorial for this button.
     * To be implemented by subclasses.
     * @protected
     */
    protected showTutorial() {}

    /**
     * Internal handler for the game over event. Calls the `gameOver` method.
     * @param puzzle The Puzzle instance.
     * @private
     */
    private _gameOverHandler = (puzzle: Puzzle) => {
        this.gameOver(puzzle);
    };

    /**
     * Callback function executed when the game is over.
     * Sets the game over state for the button.
     * @virtual
     * @param puzzle The Puzzle instance.
     */
    gameOver(puzzle: Puzzle) {
        this._gameOver = true;
    }

    /**
     * Resets the button's cooldown state and visual appearance.
     */
    clearCooldown() {
        this.currentCooldown = this.setCooldown();

        this.myInventoryDisplayText = formatNumber(this.currentInventoryCount);
        this.myInventoryDisplayColor = [1, 1, 1, 1];

        this.lastTurn = 0;
        this.isInactive = false;
        this.firstUse = false;
        this.sliderValue = 0;
        this.updateMaterial();
        this.notifyPropertyChanged('everything');
    }
    /**
     * Handles the button click event. Spends inventory, triggers the button action,
     * and updates the button's state and visual appearance.
     * @virtual
     */
    onClick() {
        // TK: The original disabled the collider when game over. This has a similar effect
        if (this._gameOver) {
            return;
        }
        // if cooldown, and active, and you have enough
        if (
            (this.lastTurn >= this.currentCooldown || this.firstUse == false) &&
            this.isInactive == false &&
            this._time > this._timer &&
            this.spendInventory(this.statInventory, this.amountSpent)
        ) {
            // update text count
            this.currentInventoryCount = Statistics.instance.getTransactional(
                this.statInventory
            );

            this.myInventoryDisplayText = formatNumber(this.currentInventoryCount);

            //play button press FX if there is one
            // if (buttonPressFX)
            // {
            // 	Instantiate(buttonPressFX, transform.position, Quaternion.identity);
            // }

            this.buttonAction();

            this.isInactive = true;

            if (this.currentInventoryCount > 0) {
                // turn on mask
                this.sliderValue = 1;
                this.updateMaterial();

                // "inactive"
                this.myInventoryDisplayColor = [0.25, 0.25, 0.25, 1];
            }

            this.lastTurn = 0;

            if (this.firstUse == false) {
                this.firstUse = true;
            }
            // trigger update for the UI.
            this.notifyPropertyChanged('everything');
        }
    }

    /**
     * The specific action performed when the button is clicked.
     * To be implemented by subclasses.
     * @virtual
     */
    buttonAction() {
        // what button does goes here
    }

    /**
     * Updates the button's visual state based on the current inventory count and cooldown status.
     * Notifies the UI about the changes.
     * @virtual
     */
    notifyStatsChanged() {
        if (Statistics.instance.getTransactional(this.statInventory) <= 0) {
            this.haveFunds = false;
            this.sliderValue = 0;
            this.myInventoryDisplayText = '0';
        } else {
            this.haveFunds = true;
            if (this.isInactive == false && this._gameOver == false) {
                this.sliderValue = 0;
            }

            this.myInventoryDisplayText = formatNumber(this.currentInventoryCount);
        }

        if (this.isInactive == true) {
            this.myInventoryDisplayColor = [0.25, 0.25, 0.25, 1];
        } else {
            this.myInventoryDisplayColor = [1, 1, 1, 1];
        }

        this.updateMaterial();
        // trigger update for the UI.
        this.notifyPropertyChanged('everything');
    }

    private _onStatsChanged = () => {
        const invCount = Statistics.instance.getTransactional(this.statInventory);

        if (invCount != this.currentInventoryCount) {
            this.currentInventoryCount = invCount;
            this.myInventoryDisplayText = formatNumber(this.currentInventoryCount);
            this.notifyPropertyChanged('everything');
        }
    };
}
